/**
 * Probe-ROW readers over a d1-crud `results` array, composing the record and string guard leaves. Homed in `const/`
 * for the guards' own standing reason: the consuming node-handler leaves are pure `utils/` modules that may not
 * value-import a `utils/` sibling, and `const/record-guards.ts` itself is a pure leaf with NO value imports -- so the
 * composed readers live one leaf up, where composing two const siblings costs nothing.
 */

import { asArrayOrEmpty } from '@federa/const/array-guards';
import { isD1ColumnTrue } from '@federa/const/d1-boolean-column';
import { asRecordOrNull, rowsOrEmpty, rowString } from '@federa/const/record-guards';
import { nonBlankStringOrNull, nonEmptyStringOrNull } from '@federa/const/string-guards';

/** First row of a d1-crud `results` array as a record, or null -- the probe-consuming step handlers' shared read.
 * @runtime-derived-ssot-helper (const-leaf-pinned): layer-floor arm, stated in full in the module docstring above --
 * the consumers are pure `utils/` leaves that may not value-import a `utils/` sibling. */
export function firstRowOrNull(rows: unknown): Record<string, unknown> | null {
	return asRecordOrNull(rowsOrEmpty(rows)[0]);
}

/** The first row's `id` as a non-empty string, or null -- the "did the lookup find it, and which one" read a probe step
 *  answers. An absent read, an empty result set, a non-record row and an empty or non-string id all read null.
 *  @runtime-derived-ssot-helper (const-leaf-pinned): same layer-floor arm as its siblings; the wire-receipt conversation
 *  extractor (a pure `utils/` leaf) and the correction reversal probe (`tools/ledger/`) each spelled
 *  `nonEmptyStringOrNull(rows[0]?.id)` over their own array coercion. */
export function firstRowIdOrNull(rows: unknown): string | null {
	return nonEmptyStringOrNull(firstRowOrNull(rows)?.id);
}

/**
 * First row of BATCH SLOT `index` as a record, or null -- the same read one slot deeper, for a handler consuming the
 * per-statement `results` an `executeD1Crud` BATCH echoed rather than a single statement's rows.
 *
 * THE SLOT INDEX STAYS AT THE CALL SITE and no default is offered: `results` is POSITIONAL over the batch's statement
 * list, so which slot a gate reads is that gate's own contract (each names its slots in its docstring). A default
 * would let a handler read slot 0 without saying so.
 *
 * @runtime-derived-ssot-helper (const-leaf-pinned): same layer-floor arm as `firstRowOrNull` above -- the consumers are
 * pure `utils/` gate leaves that may not value-import a `utils/` sibling, which is why the fold lands here and not in
 * one of them. Two of those leaves (`utils/groups/group-closure-shapes.ts`, `utils/groups/group-settings-shapes.ts`)
 * had each declared this body verbatim, each carrying a placard explaining the respelling; `tools/groups/group-update-prepare.ts`
 * spelled the same composition inline.
 */
export function slotRowOrNull(results: unknown, index: number): Record<string, unknown> | null {
	return firstRowOrNull(asArrayOrEmpty(results)[index]);
}

/**
 * EVERY row of batch slot `index`, for a gate whose slot is a PAGE rather than a go/no-go probe.
 *
 * The sibling above answers the existence question -- "did this statement match anything" -- which is what a
 * membership or status probe asks. A slot that projects one row PER ancestor is a different shape: reading only its
 * first row would decide a whole-chain question off the nearest link, so the two reads are separate rather than one
 * with a flag.
 *
 * SLOT INDEX STAYS AT THE CALL SITE, no default, for the identical contract reason.
 * @runtime-derived-ssot-helper (const-leaf-pinned): composes `rowsOrEmpty` one slot deeper, homed here for the same
 * layer-floor arm -- its consumer is a pure `utils/` gate leaf that may not value-import a `utils/` sibling.
 */
export function slotRowsOrEmpty(results: unknown, index: number): Record<string, unknown>[] {
	return rowsOrEmpty(asArrayOrEmpty(results)[index]);
}

/** Whether batch statement `index` CHANGED a row: the `changes` list of a d1-crud write envelope read at that slot, a
 *  missing or non-numeric count as none. The `Number(asArrayOrEmpty(changes)[i] ?? 0) > 0` read the order clock-in,
 *  clock-out and completion flows and the booking-request write each spelled (2026-09-28 fold).
 *  @runtime-derived-ssot-helper (const-leaf-pinned): the write-side sibling of {@link slotRowOrNull}, same slot contract. */
export function slotChanged(changes: unknown, index: number): boolean {
	return Number(asArrayOrEmpty(changes)[index] ?? 0) > 0;
}

/** The per-statement `results` of a d1-crud BATCH envelope -- `results[i]` is the i-th statement's own rows array, so
 *  order survives the read -- or empty when the envelope carried none.
 *  @runtime-derived-ssot-helper (const-leaf-pinned): the batch-arm read the qa reconcile and the qa hydration each
 *  spelled through a cast, here over `asRecordOrNull` so no caller casts the envelope. */
export function batchStatementResults(out: unknown): unknown[] {
	return asArrayOrEmpty(asRecordOrNull(out)?.results);
}

/**
 * The `total` column off a d1-crud AGGREGATE result set -- any single-row `... AS total` projection, `COUNT(*)` and
 * `SUM(...)` alike -- as a number. The consumers are both kinds: the paginated shapers read a `COUNT(*) AS total`, and
 * the refund cap gate reads `SELECT_OPEN_FLAGGED_SQL`'s `COALESCE(SUM(debit), 0) AS total`. A
 * non-array result set, an empty one, a NON-RECORD first element and a first row carrying no `total` ALL read as 0 --
 * the same answer an empty table gives, so a caller that must tell a BROKEN read from an empty one guards the
 * container itself rather than reading it out of this number.
 *
 * THE ROW IS RECORD-GUARDED, through the same-file {@link firstRowOrNull} rather than a bare index. The predecessor
 * indexed `rowsOrEmpty(...)[0]` -- itself a CAST -- and reached 0 for a non-record element by MEMBER MISS: a truthy
 * scalar was accepted as a row and `first.total` merely happened to be `undefined` on it. Same answer, no proof. The
 * guarded route is answer-preserving for every shape a D1 result set can hold (measured shape-for-shape) and
 * fail-closed for the shapes it cannot: only a truthy NON-RECORD carrying a live `total` -- a prototype-polluted
 * primitive, a function -- read a number before and reads 0 now, which is the fail-closed direction on a money read.
 *
 * @runtime-derived-ssot-helper (const-leaf-pinned): same layer-floor arm as its siblings above. Hand-rolling this
 * three-way collapse per call site is the silent-wrong-total class the paginated shapers exist to prevent, and it was
 * already spelled twice -- privately in `utils/database/paginated-count-envelope.ts` and again as a local readTotal
 * in `utils/actions/action-list-handlers.ts`, which reads TWO count sets through it (filtered and unfiltered) and so
 * could not have adopted the other spelling even if a utils sibling import were legal. That local is unbackticked
 * deliberately: the fold reaped it, so the name is a record rather than a symbol a reader can chase.
 */
export function countRowTotal(countResults: unknown): number {
	const first = firstRowOrNull(countResults);
	return first !== null && typeof first.total !== 'undefined' ? Number(first.total) : 0;
}

/**
 * The named member as a string with CONTENT (non-empty after trimming), or null -- `rowString`'s trim-aware sibling
 * for readers that must not treat a whitespace-only column as a value: a whitespace-only `address_line_1` passed to
 * the title-order enqueue would dispatch a real search at cost against nothing.
 *
 * @runtime-derived-ssot-helper (const-leaf-pinned): same layer-floor arm as its sibling above.
 */
export function rowNonEmptyString(rec: Record<string, unknown> | null | undefined, key: string): string | null {
	return nonBlankStringOrNull(rowString(rec, key));
}

/**
 * D1 answers a projected `EXISTS(...)` or a boolean-shaped column as 0/1; a non-numeric, string, or absent value
 * reads FALSE, which is the fail-closed direction for a permission or deletion flag -- an unreadable shape can never
 * be mistaken for "yes". STRICT on purpose: the `'1'`-tolerant reads some legacy rows need are a DIFFERENT contract
 * and stay at their own sites. Extracted 2026-09-01 (dedup EXTRACTION-BLOCKED fold) from byte-identical
 * module-private copies in `utils/groups/ancestor-admission.ts` and `utils/knowledgebase/ai-search-qa-reconcile-plan.ts`.
 *
 * THIS IS THE KEYED FORM of `isD1ColumnTrue` (`const/d1-boolean-column.ts`) and delegates to it: the envelope itself --
 * which spellings a set cell can arrive as, and the strict/text-tolerant split this docstring names -- is that leaf's
 * decision, shared with the `utils/`, `tools/`, `spa/` and `mfe/` readers that hold a typed row rather than a record
 * and so cannot use a key. What stays here is only the RECORD-plus-KEY access shape.
 *  @runtime-derived-ssot-helper (const-leaf-pinned): value-imported by two `utils/` leaves
 *  (`groups/ancestor-admission`, `knowledgebase/ai-search-qa-reconcile-plan`) that may not share a `utils/` sibling.
 */
export function rowFlag(row: Record<string, unknown>, key: string): boolean {
	return isD1ColumnTrue(row[key]);
}
